// Capa HTTP de "De qué documentos lee Rellenar con IA" (`/admin/ia`). El estado
// vive en TanStack Query (ver `src/hooks/useRellenoDocumentos.ts`).

import { apiFetch } from "./api";

/**
 * Un tipo de documento del catálogo.
 *
 * `categoria` es `null` cuando el código guardado ya no existe en el catálogo
 * (dado de baja): el servidor lo sigue listando con el código por nombre para
 * que se pueda ver y quitar.
 */
export interface DocumentoDelCatalogo {
  codigo: string;
  nombre: string;
  categoria: "legal" | "historico" | null;
}

/** Un documento dentro de la lista de una pestaña, con su nota opcional. */
export interface DocumentoDeLaPestana extends DocumentoDelCatalogo {
  /** Qué buscar en este documento; se imprime junto a él en el contexto de la IA. */
  nota: string | null;
}

export interface PestanaDelRelleno {
  id: string;
  etiqueta: string;
  /** La lista guardada difiere de la original del código. */
  personalizado: boolean;
  /** En orden de prioridad: el primero gana una contradicción. */
  documentos: DocumentoDeLaPestana[];
}

/** Lo que se edita y se envía de cada documento: su código y su nota. */
export interface EntradaDeLaLista {
  codigo: string;
  nota: string;
}

export interface RellenoDocumentos {
  pestanas: PestanaDelRelleno[];
  catalogo: DocumentoDelCatalogo[];
}

export async function fetchRellenoDocumentos(): Promise<RellenoDocumentos> {
  const { data } = await apiFetch<{ data: RellenoDocumentos }>("/api/ia/relleno-documentos");
  return data;
}

export async function guardarRellenoDocumentosRequest(
  pestana: string,
  lista: readonly EntradaDeLaLista[],
): Promise<PestanaDelRelleno> {
  // La nota en blanco viaja como `null`: el servidor guarda "sin nota" y no espacios.
  const documentos = lista.map(({ codigo, nota }) => ({ codigo, nota: nota.trim() || null }));
  const { data } = await apiFetch<{ data: PestanaDelRelleno }>(
    `/api/ia/relleno-documentos/${encodeURIComponent(pestana)}`,
    { method: "PUT", body: JSON.stringify({ documentos }) },
  );
  return data;
}

export function entradasDe(documentos: readonly DocumentoDeLaPestana[]): EntradaDeLaLista[] {
  return documentos.map(({ codigo, nota }) => ({ codigo, nota: nota ?? "" }));
}

/** Mueve el elemento de `indice` una posición (`-1` sube, `1` baja). */
export function moverEntrada<T>(lista: readonly T[], indice: number, paso: -1 | 1): T[] {
  const destino = indice + paso;
  if (destino < 0 || destino >= lista.length) return [...lista];
  const copia = [...lista];
  [copia[indice], copia[destino]] = [copia[destino]!, copia[indice]!];
  return copia;
}

export function mismasEntradas(
  a: readonly EntradaDeLaLista[],
  b: readonly EntradaDeLaLista[],
): boolean {
  return (
    a.length === b.length &&
    a.every((entrada, i) => entrada.codigo === b[i]!.codigo && entrada.nota.trim() === b[i]!.nota.trim())
  );
}
